import {
  IClinicInternalExtension,
  IClinicNumber,
  IClinicPhoneDevice,
  IClinicVoicemailSettings,
  ITwilioPortRequest,
  IWorkingHours,
  ClinicNumberRow,
  ClinicInternalExtensionRow,
} from '@dataTypes/clinic.type'
import {
  SetStateAction,
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  cancelPortRequestAction,
  deleteInternalExtensionAction,
  deletePhoneDeviceAction,
  fetchPhoneManagementData,
  refreshPortRequestStatusAction,
  releaseClinicNumberAction,
} from '@store/actions/settings-communication.action'
import { FilterSelection } from '@enamelai/design-system'
import { safeParseMethods } from '@utils/phoneManagement.utils'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'

const normalizeString = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

const portStatusMatches = (status: string, selected: string[]): boolean => {
  const r = normalizeString(status)
  return selected.some((sel) => r === normalizeString(sel))
}

const isPortedClinicNumber = (n: ClinicNumberRow): boolean =>
  !!n.twilio_port_request_id && String(n.twilio_port_request_id).trim() !== ''

const PHONE_MANAGEMENT_LOADING_CONTAINER = '.scp-phone-management-container'

const getAsyncErrorMessage = (err: unknown, fallback: string): string => {
  if (typeof err === 'string' && err.trim()) return err
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message: unknown }).message
    if (typeof m === 'string' && m.trim()) return m
  }
  return fallback
}

const canReleasePurchasedClinicNumber = (n: ClinicNumberRow): boolean => {
  if (isPortedClinicNumber(n)) return false
  const hasPurchased =
    !!n.purchased_date && String(n.purchased_date).trim() !== ''
  return hasPurchased && n.status === 'Active'
}

const carrierMatches = (
  carrier: string | undefined,
  selected: string[],
): boolean => {
  if (selected.length === 0) return true
  const c = (carrier || '').trim()
  if (!c) {
    return selected.includes('Other')
  }
  const lower = c.toLowerCase()
  return selected.some((label) => {
    if (label === 'Other') {
      const isOther = !(
        /at&t|att\b/.test(lower) ||
        /t-mobile|tmobile/.test(lower) ||
        /verizon/.test(lower) ||
        /sprint/.test(lower)
      )
      return isOther
    }
    if (label === 'AT&T') return /at&t|att\b/i.test(c)
    if (label === 'T-Mobile') return /t-mobile|tmobile/i.test(c)
    if (label === 'Verizon') return /verizon/i.test(c)
    if (label === 'Sprint') return /sprint/i.test(c)
    return false
  })
}

export type PhoneManagementMode =
  | 'list'
  | 'editPhoneNumber'
  | 'addExtension'
  | 'addNumber'
  | 'addDevice'

export type PhoneEditorState = {
  draft: Partial<ClinicNumberRow>
  workingHours: IWorkingHours[]
  forwarding: Record<string, unknown>
  voicemail: Partial<IClinicVoicemailSettings>
}

const defaultVoicemail = (): Partial<IClinicVoicemailSettings> => ({
  enabled: false,
  transcribe_enabled: false,
  greeting_type: 'audio',
})

interface IPhoneManagementHooks {
  beginEditPhoneLine: (n: ClinicNumberRow) => void
  cancelPortRequest: (p: ITwilioPortRequest) => Promise<boolean>
  closeResubmitPortModal: () => void
  openResubmitPortModal: (p: ITwilioPortRequest) => void
  resubmitPortRequestData: ITwilioPortRequest | null
  cancelToList: () => void
  clinicId: string
  clinicNumbers: IClinicNumber[]
  deleteInternalExtension: (id: string) => Promise<boolean>
  deletePhoneDevice: (id: string) => Promise<boolean>
  deviceLabel: (id: string) => string
  extensionLabel: (clinicInternalExtensionId: string) => string
  extensionFilters: FilterSelection
  extensionSearchQuery: string
  filteredClinicNumbers: ClinicNumberRow[]
  filteredInternalExtensions: ClinicInternalExtensionRow[]
  filteredPortRequests: ITwilioPortRequest[]
  handleAddNumber: (id: string) => void
  internalExtensions: IClinicInternalExtension[]
  isPortedClinicNumber: (n: ClinicNumberRow) => boolean
  canReleasePurchasedClinicNumber: (n: ClinicNumberRow) => boolean
  loading: boolean
  dataReady: boolean
  mainFilters: FilterSelection
  mainSearchQuery: string
  method: 'port' | 'purchase' | null
  mode: PhoneManagementMode
  onHidePortNumberModal: () => void
  phoneDevices: IClinicPhoneDevice[]
  phoneEditor: PhoneEditorState | null
  portRequests: ITwilioPortRequest[]
  refreshPortRequestStatus: (p: ITwilioPortRequest) => Promise<boolean>
  releaseClinicNumber: (n: ClinicNumberRow) => Promise<boolean>
  runAsync: <T>(
    operation: () => Promise<T>,
    messages: {
      successTitle?: string
      successMessage?: string
      errorTitle: string
      errorFallbackMessage: string
    },
  ) => Promise<T | undefined>
  selectedPhoneLine: ClinicNumberRow | null
  selectedExtension: ClinicInternalExtensionRow | null
  setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
  setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
  setPhoneEditor: Dispatch<SetStateAction<PhoneEditorState | null>>
  setSelectedPhoneLine: Dispatch<SetStateAction<ClinicNumberRow | null>>
  setSelectedExtension: Dispatch<
    SetStateAction<ClinicInternalExtensionRow | null>
  >
  startAddExtension: () => void
  startAddDevice: () => void
  startEditExtension: (e: ClinicInternalExtensionRow) => void
  setExtensionFilters: (filters: FilterSelection) => void
  setExtensionSearchQuery: (query: string) => void
  setMainFilters: (filters: FilterSelection) => void
  setMainSearchQuery: (query: string) => void
  setMode: (m: PhoneManagementMode) => void
  switchTab: (_tab: string) => void
  tab: 'main' | 'port'
}

const usePhoneManagement = (): IPhoneManagementHooks => {
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { clinicNumbers, internalExtensions, phoneDevices, portRequests } =
    useAppSelector((state) => state.settingsCommunication)
  const loading = useAppSelector((state) => state.settingsCommunication.loading)
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const [mode, setMode] = useState<PhoneManagementMode>('list')
  const [selectedPhoneLine, setSelectedPhoneLine] =
    useState<ClinicNumberRow | null>(null)
  const [selectedExtension, setSelectedExtension] =
    useState<ClinicInternalExtensionRow | null>(null)
  const [phoneEditor, setPhoneEditor] = useState<PhoneEditorState | null>(null)
  const [method, setMethod] = useState<'port' | 'purchase' | null>(null)
  const [tab, setTab] = useState<'main' | 'port'>('main')
  const [extensionSearchQuery, setExtensionSearchQuery] = useState('')
  const [extensionFilters, setExtensionFilters] = useState<FilterSelection>({})
  const [mainSearchQuery, setMainSearchQuery] = useState('')
  const [mainFilters, setMainFilters] = useState<FilterSelection>({})
  const [resubmitPortRequestData, setResubmitPortRequestData] =
    useState<ITwilioPortRequest | null>(null)
  const [dataReady, setDataReady] = useState(false)
  const loadGen = useRef(0)

  useSettingsSubpageLoading(!dataReady)

  const cancelToList = useCallback(() => {
    setMode('list')
    setSelectedPhoneLine(null)
    setSelectedExtension(null)
    setPhoneEditor(null)
  }, [])

  const runAsync = useCallback(
    async <T>(
      operation: () => Promise<T>,
      messages: {
        successTitle?: string
        successMessage?: string
        errorTitle: string
        errorFallbackMessage: string
      },
    ): Promise<T | undefined> => {
      showLoading({ container: PHONE_MANAGEMENT_LOADING_CONTAINER })
      let shouldCancelOnSuccess = false
      try {
        const result = await operation()
        if (messages.successTitle && messages.successMessage) {
          showToast(
            createSuccessToast(messages.successTitle, messages.successMessage),
          )
          shouldCancelOnSuccess = true
        }
        return result
      } catch (err: unknown) {
        const msg = getAsyncErrorMessage(err, messages.errorFallbackMessage)
        showToast(createFailedToast(messages.errorTitle, msg))
        return undefined
      } finally {
        hideLoading(PHONE_MANAGEMENT_LOADING_CONTAINER)
        if (shouldCancelOnSuccess) {
          window.requestAnimationFrame(() => {
            cancelToList()
          })
        }
      }
    },
    [showLoading, hideLoading, showToast, cancelToList],
  )

  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')

  useClinicChange(clinicId, () => {
    loadGen.current += 1
    setDataReady(false)
    setMode('list')
    setSelectedPhoneLine(null)
    setSelectedExtension(null)
    setPhoneEditor(null)
  })

  const filteredInternalExtensions = useMemo(() => {
    const q = extensionSearchQuery.trim().toLowerCase()
    const teamSelection = extensionFilters.team ?? []
    const filterByAllTeams = teamSelection.length === 0

    return internalExtensions
      .map((e) => ({ ...e, id: e.clinic_internal_extension_id ?? '' }))
      .filter((e) => {
        if (!filterByAllTeams) {
          const team = (e.team || '').trim()
          if (!teamSelection.includes(team)) return false
        }
        if (!q) return true
        const haystack = [e.name, e.phone_number, e.team, e.extension_number]
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
          .join(' ')
          .toLowerCase()
        return haystack.includes(q)
      })
  }, [internalExtensions, extensionSearchQuery, extensionFilters])

  const filteredClinicNumbers = useMemo(() => {
    const query = mainSearchQuery.trim().toLowerCase()
    const _featureSel = mainFilters.feature ?? []

    return clinicNumbers
      .map((n) => ({ ...n, id: n.clinic_number_id ?? '' }))
      .filter((n) => {
        if (_featureSel.length > 0) {
          const methods = safeParseMethods(n.contact_methods).map((m) =>
            m.toLowerCase(),
          )
          const featureSel = _featureSel.map((f) => f.toLowerCase())
          if (!featureSel.some((w) => methods.includes(w))) return false
        }
        if (!query) return true
        const haystack = [n.line_name, n.phone_number]
          .filter((v): v is string => typeof v === 'string' && v.length > 0)
          .join(' ')
          .toLowerCase()
        return haystack.includes(query)
      })
  }, [clinicNumbers, mainSearchQuery, mainFilters])

  const filteredPortRequests = useMemo(() => {
    const query = mainSearchQuery.trim().toLowerCase()
    const statusSel = mainFilters.status ?? []
    const carrierSel = mainFilters.carrier ?? []

    return portRequests
      .filter((p) => {
        if (p.is_latest === false) {
          return false
        }
        if (statusSel.length > 0 && !portStatusMatches(p.status, statusSel)) {
          return false
        }
        const pCarrier = (
          p as ITwilioPortRequest & { phone_carrier?: string | null }
        ).phone_carrier
        if (
          carrierSel.length > 0 &&
          !carrierMatches(pCarrier ?? undefined, carrierSel)
        ) {
          return false
        }
        if (!query) return true
        const haystack = p.phone_numbers
          .map((num) => normalizePhoneNumber(num))
          .filter((v) => v.length > 0)
          .join(' ')
          .toLowerCase()
        return haystack.includes(query)
      })
      .sort((a, b) => {
        const aTime = Date.parse(a.date_time_created_at ?? '')
        const bTime = Date.parse(b.date_time_created_at ?? '')
        return (
          (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime)
        )
      })
  }, [portRequests, mainSearchQuery, mainFilters])

  const refresh = useCallback(async (): Promise<void> => {
    const gen = ++loadGen.current
    if (!clinicId) {
      setDataReady(true)
      return
    }
    try {
      await dispatch(fetchPhoneManagementData(clinicId)).unwrap()
    } catch (err: unknown) {
      const msg = getAsyncErrorMessage(
        err,
        'Unable to load phone numbers, extensions, and port requests.',
      )
      showToast(createFailedToast('Could not load phone settings', msg))
    } finally {
      if (loadGen.current === gen) setDataReady(true)
    }
  }, [clinicId, dispatch, showToast])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    setMode('list')
    setSelectedPhoneLine(null)
    setSelectedExtension(null)
    setPhoneEditor(null)
  }, [clinicId])

  const beginEditPhoneLine = useCallback((n: ClinicNumberRow) => {
    setSelectedPhoneLine(n)
    setPhoneEditor({
      draft: { ...n },
      workingHours: n.workingHours || [],
      forwarding: { ...(n.callForwardingRules || {}) },
      voicemail: { ...defaultVoicemail(), ...(n.voicemailSettings || {}) },
    })
    setMode('editPhoneNumber')
  }, [])

  const deleteInternalExtension = async (id: string): Promise<boolean> => {
    const result = await runAsync(
      () => dispatch(deleteInternalExtensionAction(id)).unwrap(),
      {
        successTitle: 'Extension removed',
        successMessage: 'The internal extension was deleted.',
        errorTitle: 'Could not delete extension',
        errorFallbackMessage:
          'Unable to remove this extension. Please try again.',
      },
    )
    return result !== undefined
  }

  const deletePhoneDevice = async (id: string): Promise<boolean> => {
    const result = await runAsync(
      () =>
        dispatch(deletePhoneDeviceAction({ id, clinic_id: clinicId })).unwrap(),
      {
        successTitle: 'Device removed',
        successMessage: 'The phone device was removed.',
        errorTitle: 'Could not remove device',
        errorFallbackMessage: 'Unable to remove this device. Please try again.',
      },
    )
    return result !== undefined
  }

  const refreshPortRequestStatus = useCallback(
    async (p: ITwilioPortRequest): Promise<boolean> => {
      const result = await runAsync(
        () =>
          dispatch(
            refreshPortRequestStatusAction({ portRequest: p, clinicId }),
          ).unwrap(),
        {
          successTitle: 'Status updated',
          successMessage: 'Port request status was refreshed.',
          errorTitle: 'Could not refresh status',
          errorFallbackMessage:
            'Unable to refresh port request status. Please try again.',
        },
      )
      return result !== undefined
    },
    [clinicId, dispatch, runAsync],
  )

  const cancelPortRequest = useCallback(
    async (p: ITwilioPortRequest): Promise<boolean> => {
      const result = await runAsync(
        () =>
          dispatch(
            cancelPortRequestAction({ portRequest: p, clinicId }),
          ).unwrap(),
        {
          successTitle: 'Port request cancelled',
          successMessage: 'The port request was cancelled.',
          errorTitle: 'Could not cancel port request',
          errorFallbackMessage:
            'Unable to cancel this port request. Please try again.',
        },
      )
      return result !== undefined
    },
    [clinicId, dispatch, runAsync],
  )

  const openResubmitPortModal = useCallback((p: ITwilioPortRequest): void => {
    setResubmitPortRequestData(p)
  }, [])

  const closeResubmitPortModal = useCallback((): void => {
    setResubmitPortRequestData(null)
  }, [])

  const releaseClinicNumber = useCallback(
    async (n: ClinicNumberRow): Promise<boolean> => {
      if (!canReleasePurchasedClinicNumber(n)) return false
      const result = await runAsync(
        () =>
          dispatch(
            releaseClinicNumberAction({ clinicNumber: n, clinicId }),
          ).unwrap(),
        {
          successTitle: 'Number released',
          successMessage: 'The phone number was released from your account.',
          errorTitle: 'Could not release number',
          errorFallbackMessage:
            'Unable to release this number. Please try again.',
        },
      )
      return result !== undefined
    },
    [clinicId, dispatch, runAsync],
  )

  const startEditExtension = (e: ClinicInternalExtensionRow): void => {
    setSelectedExtension(e)
    setMode('addExtension')
  }

  const startAddExtension = (): void => {
    setSelectedExtension({
      id: '',
      clinic_id: clinicId,
      name: '',
      team: '',
      extension_number: '',
      phone_number: '',
    })
    setMode('addExtension')
  }

  const startAddDevice = (): void => {
    setMode('addDevice')
  }

  const deviceLabel = (id: string): string => {
    const n = clinicNumbers.find((x) => x.clinic_number_id === id)
    if (!n) return '—'
    return `${n.line_name || 'Line'} (${n.phone_number})`
  }

  const extensionLabel = (clinicInternalExtensionId: string): string => {
    const e = internalExtensions.find(
      (x) => x.clinic_internal_extension_id === clinicInternalExtensionId,
    )
    if (!e) return '—'
    return `${e.name} (${e.extension_number})`
  }

  const handleAddNumber = (id: string): void => {
    setMethod(id as 'port' | 'purchase')
  }

  const onHidePortNumberModal = (): void => {
    setMethod(null)
  }

  const switchTab = (_tab: string): void => {
    setTab(_tab as 'main' | 'port')
    setMainSearchQuery('')
    setMainFilters({})
  }

  return {
    beginEditPhoneLine,
    cancelPortRequest,
    closeResubmitPortModal,
    openResubmitPortModal,
    resubmitPortRequestData,
    cancelToList,
    clinicId,
    clinicNumbers,
    canReleasePurchasedClinicNumber,
    deleteInternalExtension,
    deletePhoneDevice,
    deviceLabel,
    extensionLabel,
    extensionFilters,
    extensionSearchQuery,
    filteredClinicNumbers,
    filteredInternalExtensions,
    filteredPortRequests,
    handleAddNumber,
    internalExtensions,
    isPortedClinicNumber,
    loading,
    dataReady,
    mainFilters,
    mainSearchQuery,
    method,
    mode,
    onHidePortNumberModal,
    phoneDevices,
    phoneEditor,
    portRequests,
    refreshPortRequestStatus,
    releaseClinicNumber,
    runAsync,
    selectedPhoneLine,
    selectedExtension,
    setExtensionFilters,
    setExtensionSearchQuery,
    setMainFilters,
    setMainSearchQuery,
    setHeaderButtons,
    setHeaderTitle,
    setPhoneEditor,
    setSelectedPhoneLine,
    setSelectedExtension,
    startAddExtension,
    startAddDevice,
    startEditExtension,
    setMode,
    switchTab,
    tab,
  }
}

export default usePhoneManagement
